import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatRupiah } from '../../utils/printHelper';
import { invoiceAmounts } from '../../utils/invoiceAmounts';
import { SignedAmountInput } from '../SignedAmountInput';

export function SiapProsesDialog({
  open, onOpenChange, invoice,
  pphType, setPphType, pphPercentage, setPphPercentage,
  adminBank, setAdminBank,
  ppnPercentage, setPpnPercentage,
  pembulatan, setPembulatan,
  potonganRetur, setPotonganRetur,
  onSubmit, loading
}) {
  if (!invoice) return null;

  const a = invoiceAmounts(invoice, {
    pembulatan: pembulatan !== undefined ? pembulatan : invoice.pembulatan,
    potonganRetur: potonganRetur || 0,
    ppnPercentage: ppnPercentage !== undefined ? ppnPercentage : invoice.ppn_percentage,
    pphType: pphType || null,
    pphPercentage,
    adminBank,
  });
  const { subtotal: subtotalItems, pembulatan: pembulatanVal, potonganRetur: potonganVal, dpp, ppnPercentage: ppnPct, ppnAmount, biayaAngkut, dpAmount, totalTagihan, pphAmount } = a;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Siap Proses - Input Potongan</DialogTitle>
          <p className="text-sm text-slate-600">Edit PPn, Pembulatan, PPh, dan Admin Bank sebelum submit ke Approval</p>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-slate-500">No. Invoice</p><p className="font-mono font-semibold">{invoice.invoice_number}</p></div>
              <div><p className="text-slate-500">Supplier</p><p className="font-semibold">{invoice.supplier_name}</p></div>
            </div>
          </div>

          {invoice.uses_va && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
              <p className="font-semibold text-amber-800 mb-1">Pembayaran via Virtual Account</p>
              <p><span className="text-slate-500">Nomor VA:</span> <span className="font-mono font-bold text-amber-700">{invoice.va_nomor || '-'}</span></p>
              <p><span className="text-slate-500">Nama VA:</span> <span className="font-bold text-amber-700">{invoice.va_nama || '-'}</span></p>
            </div>
          )}

          {/* PPn Edit */}
          {invoice.has_ppn && setPpnPercentage && (
            <div className="space-y-2 border rounded-lg p-4 bg-blue-50">
              <Label className="text-blue-700 font-medium">Persentase PPn (%)</Label>
              <Input type="number" value={ppnPercentage} onChange={(e) => setPpnPercentage(parseFloat(e.target.value) || 0)} className="w-32" step="0.1" data-testid="siap-proses-ppn-pct" />
            </div>
          )}

          {/* Pembulatan Edit */}
          {setPembulatan && (
            <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
              <Label className="text-slate-700 font-medium">Pembulatan (Rp)</Label>
              <SignedAmountInput
                key={invoice.id}
                value={pembulatan}
                onChange={setPembulatan}
                className="w-48 font-mono"
                data-testid="siap-proses-pembulatan"
              />
              <p className="text-xs text-slate-500">Koreksi pada total tagihan, tidak mempengaruhi DPP maupun PPn/PPh. Boleh diisi minus.</p>
            </div>
          )}

          {/* Potongan / Retur (AP only) */}
          {setPotonganRetur && (
            <div className="space-y-2 border rounded-lg p-4 bg-amber-50">
              <Label className="text-amber-700 font-medium">Potongan / Retur (Rp)</Label>
              <Input
                type="number"
                value={potonganRetur}
                onChange={(e) => setPotonganRetur(parseFloat(e.target.value) || 0)}
                className="w-48 font-mono"
                min="0"
                step="1000"
                placeholder="0"
                data-testid="siap-proses-potongan-retur"
              />
              <p className="text-xs text-amber-600">Nilai ini akan mengurangi total invoice</p>
            </div>
          )}

          {/* PPh Section */}
          <div className="space-y-2 border rounded-lg p-4 bg-rose-50">
            <div className="flex items-center gap-2">
              <Checkbox
                id="siap-proses-has-pph"
                checked={pphType !== '' && pphType !== null}
                onCheckedChange={(checked) => {
                  setPphType(checked ? 'PPh23' : '');
                  setPphPercentage(checked ? 2 : 0);
                }}
              />
              <Label htmlFor="siap-proses-has-pph" className="cursor-pointer text-rose-700 font-medium">Potongan PPh</Label>
            </div>
            {pphType && (
              <div className="space-y-3 pl-6">
                <div className="space-y-2">
                  <Label>Jenis PPh</Label>
                  <Select value={pphType} onValueChange={(value) => {
                    let defaultPct = 0;
                    if (value === 'PPh21') defaultPct = 5;
                    else if (value === 'PPh23') defaultPct = 2;
                    else if (value === 'PPh4(2)') defaultPct = 10;
                    setPphType(value);
                    setPphPercentage(defaultPct);
                  }}>
                    <SelectTrigger className="w-48" data-testid="siap-proses-pph-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PPh21">PPh 21</SelectItem>
                      <SelectItem value="PPh23">PPh 23</SelectItem>
                      <SelectItem value="PPh4(2)">PPh 4 ayat 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Persentase PPh (%)</Label>
                  <Input type="number" value={pphPercentage} onChange={(e) => setPphPercentage(parseFloat(e.target.value) || 0)} className="w-32" step="0.1" />
                </div>
              </div>
            )}
          </div>

          {/* Admin Bank */}
          <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
            <Label>Biaya Admin Bank (Rp)</Label>
            <Input type="number" value={adminBank} onChange={(e) => setAdminBank(parseFloat(e.target.value) || 0)} className="w-48" step="1000" placeholder="0" data-testid="siap-proses-admin-bank" />
          </div>

          {/* Summary */}
          <div className="bg-emerald-100 border border-emerald-300 p-4 rounded-lg space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal Item</span><span className="font-mono">Rp {formatRupiah(subtotalItems)}</span></div>
            {potonganVal > 0 && <div className="flex justify-between text-orange-700"><span>Potongan / Retur</span><span className="font-mono">- Rp {formatRupiah(potonganVal)}</span></div>}
            <div className="flex justify-between border-t pt-1"><span>DPP (Dasar Pengenaan Pajak)</span><span className="font-mono font-semibold">Rp {formatRupiah(dpp)}</span></div>
            {invoice.has_ppn && <div className="flex justify-between"><span>PPn ({ppnPct}%)</span><span className="font-mono">Rp {formatRupiah(ppnAmount)}</span></div>}
            {biayaAngkut > 0 && <div className="flex justify-between"><span>Biaya Angkut</span><span className="font-mono">Rp {formatRupiah(biayaAngkut)}</span></div>}
            {pembulatanVal !== 0 && <div className="flex justify-between"><span>Pembulatan</span><span className="font-mono">{pembulatanVal >= 0 ? '+' : ''}Rp {formatRupiah(pembulatanVal)}</span></div>}
            {dpAmount > 0 && <div className="flex justify-between text-amber-700"><span>Down Payment</span><span className="font-mono">- Rp {formatRupiah(dpAmount)}</span></div>}
            <div className="flex justify-between font-bold border-t pt-1"><span>Total Tagihan</span><span className="font-mono">Rp {formatRupiah(totalTagihan)}</span></div>
            {pphAmount > 0 && <div className="flex justify-between text-rose-700"><span>PPh ({pphType} {pphPercentage}%)</span><span className="font-mono">- Rp {formatRupiah(pphAmount)}</span></div>}
            {a.adminBank > 0 && <div className="flex justify-between text-slate-600"><span>Admin Bank</span><span className="font-mono">- Rp {formatRupiah(a.adminBank)}</span></div>}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Nilai Dibayarkan</span>
              <span className="font-mono text-emerald-700" data-testid="siap-proses-nilai-dibayarkan">Rp {formatRupiah(a.nilaiDibayarkan)}</span>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onSubmit} disabled={loading} data-testid="siap-proses-submit">
              {loading ? 'Memproses...' : 'Proses ke Payment Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


